<template>
  <div
    class="lg:shadow-md px-6 flex flex-wrap lg:flex-nowrap items-center lg:justify-around w-full bg-white fixed lg:relative bottom-0"
    style="z-index: 5">
    <template v-if="isStatusCorrect">
      <div :class="{ invisible: !documentDate }" class="px-2 py-2 flex items-center lg:w-1/3 w-full lg:order-1">
        <div class="pl-2 pt-2 flex items-center justify-center">
          <component :is="iconSelected" />
          <p class="header--subtitle pl-2 text-zt-md lg:text-zt-base text-zt-dark-300">
            <span class="lowercase font-bold">{{ messageStatusSigned + ' ' + $t("message.at") }} </span>
            <span class="pl-1">{{ documentDate }}</span>
          </p>
        </div>
      </div>
      <div class="mx-auto lg:mx-0 lg:w-1/3 order-first lg:order-2">
        <div class="flex items-center justify-center">
          <div class="hidden lg:block mr-6">
            <button data-vs-button="zoom-out" class="text-zt-primary-200 text-zt-xl mr-4 outline-none" @click="zoomOut">
              <zt-icon name="zoom-out" />
            </button>
            <button data-vs-button="zoom-in" class="text-zt-primary-200 text-zt-xl outline-none" @click="zoomIn">
              <zt-icon name="zoom-in" />
            </button>
          </div>
          <div class="">
            <button data-vs-button="last-image" class="inline-block outline-none" @click="getLastImage">
              <zt-icon name="angle-left" />
            </button>
            <p v-if="getTotalPages" class="inline-block px-1 pt-1">
              {{ page }} / {{ getTotalPages }}
            </p>
            <button data-vs-button="next-image" @click="getNextImage" class="inline-block outline-none">
              <zt-icon name="angle-right" />
            </button>
            <button data-vs-button="fullscreen" class="text-zt-primary-200 text-zt-xl outline-none"
              @click="toggleFullScreen">
              <zt-icon name="expand-arrows" />
            </button>
          </div>
        </div>
      </div>
      <div class="lg:flex justify-end hidden w-1/3 lg:order-3">
        <zt-button data-vs-button="print-document" v-if="
          document.tipo_formato === 'pdf' || document.tipo_formato === 'eform'
        " :disabled="printDisabled" class="items-center mr-2" @click="printDocument" outlined>
          <zt-icon name="print" class="text-zt-xl" />
          <span class="pl-2">{{
            $t('message.documentVisualizerComponent.printDocument')
          }}</span>
        </zt-button>
        <zt-button data-vs-button="get-pdf" v-if="
          document.tipo_formato === 'pdf' || document.tipo_formato === 'eform'
        " class="items-center" @click="getPdfFile" outlined>
          <zt-icon name="download-paper-outline" class="text-zt-xl" />
          <span class="pl-2">{{
            $t('message.documentVisualizerComponent.downloadPDF')
          }}</span>
        </zt-button>
        <template v-else>
          <div class="inline-block relative">
            <zt-button data-vs-button="download" outlined @click="showDownloadOpts = !showDownloadOpts">{{
              $t("message.DownloadAllDocuments.downloadAll") }}</zt-button>
            <div v-if="showDownloadOpts"
              class="py-2 w-full absolute border-2 border-zt-primary-200 z-50 bg-white mt-2 flex flex-col">
              <button data-vs-button="get-pdf-file" @click="getPdfFile"
                class="mb-2 text-zt-primary-200 focus:outline-none">
                {{ $t("message.DocumentsComponents.pdfFile") }}
              </button>
              <button data-vs-button="get-xml-file" @click="getXmlFile" class="text-zt-primary-200 focus:outline-none">
                {{ $t("message.DocumentsComponents.xmlFile") }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </template>
    <template v-else-if="isValidPDFDocument">
      <DocumentDetailPDF @fullscreen-change="toggleFullScreen" :getNextImage="getNextImage" :getLastImage="getLastImage"
        :refreshSignedDocument="refreshSignedDocument" :page="page" />
    </template>
    <iframe 
        id="print_frame" name="print_frame" class="hidden" />
  </div>
</template>

<script>
import DocumentDetailPDF from './DocumentDetailPDF'
import DocumentOkIcon from './SVG/DocumentOkIcon'
import DocumentNoOkIcon from './SVG/DocumentNoOkIcon'
import { mapState, mapActions } from 'vuex'
import Users from '@/apiClient/Users'
import { includes } from 'lodash'
import moment from 'moment'

export default {
  name: 'DocumentDetailHeader',
  components: {
    DocumentDetailPDF,
    DocumentNoOkIcon,
    DocumentOkIcon
  },
  inject: ['documentFileStore', 'setFile', 'zoomIn', 'zoomOut'],
  props: ['page'],
  data() {
    return {
      profile: '',
      documentDate: '',
      iddApp: 0,
      iddoc: 0,
      pdfFile: '',
      xmlFile: '',
      showDownloadOpts: false,
      printDisabled: false,
      statusSigned: [
        'FIRMADO CONFORME',
        'FIRMADO DISCONFORME',
        'PENDIENTE APROBACION',
        'NO REQUIERE FIRMA',
        'RECHAZADO'
      ]
    }
  },
  async created() {
    this.profile = new Users()

    this.idApp = this.user.aplicacion.id
    this.iddoc = this.document.id

    this.getDateSigned()
    this.getImagePage()
  },
  watch: {
    'document.firmado'(newValue) {
      if (newValue === 'FIRMADO CONFORME') {
        this.getDateSigned()
      }
    }
  },
  methods: {
    ...mapActions('documents', [
      'setImageValue',
      'setLoadingImage',
      'setCurrentPage',
      'setDocumentFile'
    ]),
    getXmlFile() {
      this.profile
        .showXmlDownload(this.idApp, this.iddoc)
        .then(res => {
          const data_uri_prefix = 'data:application/xml;base64,'
          const image = data_uri_prefix + res
          this.xmlFile = image
        })
        .then(() => {
          const element = document.createElement('a')
          element.setAttribute('href', this.xmlFile)
          element.setAttribute('download', 'document.xml')
          document.body.appendChild(element)
          element.click()
          document.body.removeChild(element)
        })
    },
    async getDateSigned() {
      const infoResponse = await this.profile.getDocumentInfo(
        this.idApp,
        this.iddoc
      )

      if (
        this.document.firmado === 'FIRMADO CONFORME' ||
        this.document.firmado === 'FIRMADO DISCONFORME'
      ) {
        this.documentDate = moment(
          infoResponse.data.results.data[0].date,
          'MM/DD/YYYY HH:mm:ss'
        ).format('DD/MM/YYYY')
      }
    },
    toggleFullScreen() {
      this.$emit('fullscreen-change')
    },
    async getImagePage() {
      try {
        this.setLoadingImage(true)
        const pagesResponse = await this.profile.getDocumentPages(
          this.idApp,
          this.iddoc
        )

        // const viewResponse = await this.profile.showDocumentView(
        //   this.idApp,
        //   this.iddoc,
        //   this.page
        // )

        // const data_uri_prefix = 'data:image/*;base64,'
        // const pageImage = data_uri_prefix + viewResponse

        // this.setImageValue(pageImage)
        this.setLoadingImage(false)
      } catch (error) {
        this.setLoadingImage(false)
      }
    },
    getNextImage() {
      if (this.page < this.documentFileStore.totalPages) {
        this.$emit('set-page', this.page + 1)
        // this.setCurrentPage(this.page + 1)
      }
    },
    getLastImage() {
      if (this.page > 1) {
        this.$emit('set-page', this.page - 1)
        // this.setCurrentPage(this.page - 1)
      }
    },
    async getPdfFile() {
      const element = document.createElement('a')
      element.setAttribute('href', this.documentFileStore.file)
      element.setAttribute('download', 'document.pdf')
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
    },
    async printDocument() {
      if (this.printDisabled) {
        return
      }
      this.printDisabled = true
      const pdfBlob = this.b64toBlob(this.documentFileStore.file)
      const objFrame = document.getElementById('print_frame')
      this.setPdfUrl(pdfBlob)
      setTimeout(function () {
        objFrame.contentWindow.focus()
        objFrame.contentWindow.print()
      }, 1000)
      this.printDisabled = false
    },
    setPdfUrl(pdf) {
      const url = URL.createObjectURL(pdf)
      document.querySelector('#print_frame').src = url
    },
    b64toBlob(dataURI) {
      const byteString = atob(dataURI.split(',')[1])
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      return new Blob([ab], { type: 'application/pdf' })
    },
    refreshSignedDocument() {
      this.setCurrentPage(1)
      this.setDocumentFile({ idApp: this.idApp, idDoc: this.iddoc })
    }
  },
  computed: {
    ...mapState(['user', 'locale', 'actions']),
    ...mapState('documents', ['document']),
    iconSelected() {
      return this.document.firmado === 'FIRMADO CONFORME' ||
        this.document.firmado === 'PENDIENTE APROBACION'
        ? DocumentOkIcon
        : DocumentNoOkIcon
    },
    isStatusCorrect() {
      return includes(this.statusSigned, this.document.firmado)
    },
    isValidPDFDocument() {
      return (
        includes(this.document.tipo_formato, 'pdf') ||
        includes(this.document.archivo.toLowerCase(), 'pdf')
      )
    },
    getTotalPages() {
      return this.documentFileStore.totalPages
    },
    messageStatusSigned() {
      return this.document.firmado === 'FIRMADO CONFORME' ?
        this.$t("message.DocumentsComponents.statusSigned.signedConformed") :
        this.$t("message.DocumentsComponents.statusSigned.signedDisconformed")
    }
  }
}
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 1023px) {
  .header--subtitle {
    padding-left: 24px;
  }
}

.header--icon {
  font-size: 32px;
}

.outline-none {
  outline: none;
}
</style>
